import * as React from "react";

import { Badge } from "../../components/ui/badge";
import { Box } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import { CountSticker } from "../../components/ui/count-sticker";
import { Checkbox } from "../../components/ui/checkbox";
import { Icon } from "../../components/ui/icon";
import { Input } from "../../components/ui/input";
import {
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuPopup,
  MenuSeparator,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
  MenuTrigger,
} from "../../components/ui/menu";
import { Text } from "../../components/ui/text";
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import type { TableFilter } from "../../hooks/use-filter-state";
import type { Omittable } from "../../lib/props";

// --- Types ---

export type DataTableFilterButtonProps<TItem = unknown> = Omittable<{
  filters: TableFilter<TItem>[];
  activeFilterCount: number;
  clearAll: () => void;
  setFilterValues: (key: string, values: string[]) => void;
  /**
   * Renders the popup body for `type: 'entity'` filters. The selector
   * machinery lives app-side; the particle only provides the submenu chrome.
   * Entity filters are skipped entirely when this prop is omitted.
   */
  renderEntityFilter?: (args: {
    filter: TableFilter<TItem>;
    setValues: (key: string, values: string[]) => void;
  }) => React.ReactNode;
  /** Hover tooltip on the chip itself. */
  tooltip?: string;
}>;

// --- Checkbox list submenu (select and date filters) ---

function CheckboxListSubmenu<TItem>({
  filter,
  onToggle,
}: {
  filter: TableFilter<TItem>;
  onToggle: (value: string) => void;
}) {
  const [search, setSearch] = React.useState("");

  const options = React.useMemo(() => {
    if (filter.type === "date") {
      return (filter.datePresets ?? []).map((p) => ({
        value: p.value,
        label: p.label,
      }));
    }
    return filter.options ?? [];
  }, [filter.type, filter.datePresets, filter.options]);

  const filtered = React.useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  return (
    <Box minWidth={220}>
      {options.length > 6 && (
        <Box padding="xs">
          <Input
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value)
            }
            placeholder="Search..."
            size="sm"
          />
        </Box>
      )}
      <Box padding="xs">
        <Box gap="none">
          {filtered.map((opt) => {
            const isChecked = filter.values.includes(opt.value);
            return (
              <Box cursor="pointer"
                key={opt.value}
                as="label"
                direction="row"
                align="center"
                gap="sm"
                paddingX="xs"
                paddingY="2xs"
                rounded="sm"
                className="hover:bg-wash-hover"
              >
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={() => onToggle(opt.value)}
                />
                <Text size="sm">{opt.label}</Text>
              </Box>
            );
          })}
          {filtered.length === 0 && (
            <Box padding="sm">
              <Text size="sm" variant="muted">
                No matches
              </Text>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// --- Submenu trigger row ---

function FilterSubTrigger<TItem>({ filter }: { filter: TableFilter<TItem> }) {
  return (
    <MenuSubTrigger>
      <Box direction="row" align="center" gap="sm">
        {filter.icon && <Icon name={filter.icon} size="lg" color="muted" />}
        <Text size="sm">{filter.label}</Text>
        <Badge color="primary" tone="solid" size="sm" className={filter.isActive ? "" : "opacity-0"}>
          {filter.isActive ? filter.values.length : 0}
        </Badge>
      </Box>
    </MenuSubTrigger>
  );
}

// --- Select / date filter submenu ---

function SelectFilterSubmenuItem<TItem>({
  filter,
  setValues,
  open,
  onOpenChange,
}: {
  filter: TableFilter<TItem>;
  setValues: (key: string, values: string[]) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const handleToggle = React.useCallback(
    (value: string) => {
      const current = filter.values;
      if (filter.multiple === false) {
        const next = current.includes(value) ? [] : [value];
        setValues(filter.key, next);
      } else {
        const next = current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value];
        setValues(filter.key, next);
      }
    },
    [filter.key, filter.values, filter.multiple, setValues],
  );

  return (
    <MenuSub open={open} onOpenChange={onOpenChange}>
      <FilterSubTrigger filter={filter} />
      <MenuSubPopup className="min-w-[360px] max-w-[100vw]" sheetTitle={filter.label}>
        <CheckboxListSubmenu filter={filter} onToggle={handleToggle} />
      </MenuSubPopup>
    </MenuSub>
  );
}

// --- Entity filter submenu (body injected by the app) ---

function EntityFilterSubmenuItem<TItem>({
  filter,
  setValues,
  open,
  onOpenChange,
  renderEntityFilter,
}: {
  filter: TableFilter<TItem>;
  setValues: (key: string, values: string[]) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  renderEntityFilter: NonNullable<
    DataTableFilterButtonProps<TItem>["renderEntityFilter"]
  >;
}) {
  return (
    <MenuSub open={open} onOpenChange={onOpenChange}>
      <FilterSubTrigger filter={filter} />
      <MenuSubPopup
        className="min-w-[500px] max-w-[100vw]"
        padding="none"
        sheetTitle={filter.label}
      >
        {renderEntityFilter({ filter, setValues })}
      </MenuSubPopup>
    </MenuSub>
  );
}

// --- Main component ---

export function DataTableFilterButton<TItem = unknown>({
  filters,
  activeFilterCount,
  clearAll,
  setFilterValues,
  renderEntityFilter,
  tooltip,
}: DataTableFilterButtonProps<TItem>): React.ReactElement | null {
  const [openSubKey, setOpenSubKey] = React.useState<string | null>(null);
  const openSubKeyRef = React.useRef<string | null>(null);

  const handleSubOpenChange = React.useCallback(
    (key: string, isOpen: boolean) => {
      const nextKey = isOpen ? key : null;
      // When switching subs, blur any focused input inside the closing sub so
      // the menu's focus tracking doesn't fight the transition.
      if (isOpen && openSubKeyRef.current && openSubKeyRef.current !== key) {
        (document.activeElement as HTMLElement | null)?.blur();
      }
      openSubKeyRef.current = nextKey;
      setOpenSubKey(nextKey);
    },
    [],
  );

  const handleMenuOpenChange = React.useCallback((menuOpen: boolean) => {
    if (!menuOpen) {
      openSubKeyRef.current = null;
      setOpenSubKey(null);
    }
  }, []);

  if (filters.length === 0) return null;

  const isActive = activeFilterCount > 0;

  const chipButton = (
    <Button
      color={isActive ? "primary" : "neutral"}
      variant={isActive ? "outline" : "ghost"}
      size="icon-xs"
      aria-pressed={isActive}
      aria-label={isActive ? `Filters, ${activeFilterCount} active` : "Filters"}
    />
  );

  // The trigger's hover handlers must land on the button element itself, so
  // the TooltipTrigger sits INSIDE the MenuTrigger's render chain (the same
  // idiom as the group-by dropdown).
  const trigger = (
    <MenuTrigger
      render={tooltip ? <TooltipTrigger render={chipButton} /> : chipButton}
    >
      <Icon name="filter" size="sm" />
      {isActive ? <CountSticker count={String(activeFilterCount)} /> : null}
    </MenuTrigger>
  );

  return (
    <Menu onOpenChange={handleMenuOpenChange}>
      {/* The toolbar filter chip: one icon-sized footprint in every state —
          borderless at rest, theme-tinted while filtered, with the count as a
          corner badge. The chip never changes width, so the toolbar it sits
          in never reflows; clearing lives in the menu's own footer. */}
      {tooltip ? (
        <Tooltip>
          {trigger}
          <TooltipPopup side="top">{tooltip}</TooltipPopup>
        </Tooltip>
      ) : (
        trigger
      )}
      <MenuPopup align="end">
        {/* MenuGroupLabel reads its labelled set from MenuGroup context and
            throws without one, so the heading and the filter rows it names
            are one group. The clear-all footer stays outside it. */}
        <MenuGroup>
          <MenuGroupLabel>Filter by</MenuGroupLabel>
          <MenuSeparator />
          {filters.map((filter) =>
            filter.type === "entity" ? (
              renderEntityFilter ? (
                <EntityFilterSubmenuItem
                  key={filter.key}
                  filter={filter}
                  setValues={setFilterValues}
                  open={openSubKey === filter.key}
                  onOpenChange={(isOpen) =>
                    handleSubOpenChange(filter.key, isOpen)
                  }
                  renderEntityFilter={renderEntityFilter}
                />
              ) : null
            ) : (
              <SelectFilterSubmenuItem
                key={filter.key}
                filter={filter}
                setValues={setFilterValues}
                open={openSubKey === filter.key}
                onOpenChange={(isOpen) =>
                  handleSubOpenChange(filter.key, isOpen)
                }
              />
            ),
          )}
        </MenuGroup>
        {isActive && (
          <>
            <MenuSeparator />
            <Box padding="xs">
              <Button variant="ghost" size="sm" onClick={clearAll}>
                <Text size={null} variant="muted">
                  Clear all filters
                </Text>
              </Button>
            </Box>
          </>
        )}
      </MenuPopup>
    </Menu>
  );
}
